import type { SupplyClass, SupplyType } from '@/types/api';

export type SupplyFormMode = 'wizard' | 'single';

export type SupplyFormStep =
  | 'identity'
  | 'productionMethods'
  | 'material'
  | 'machineCompatibility'
  | 'tracking'
  | 'productCompatibility'
  | 'photo';

export const STEP_LABELS: Record<SupplyFormStep, string> = {
  identity: '📦 Supply Identity',
  productionMethods: '🏭 Production',
  material: '🧱 Material & Physical Details',
  machineCompatibility: 'Production System',
  tracking: '📦 Purchasing',
  productCompatibility: 'Product Fit',
  photo: '🖼 Photos',
};

export const SUPPLY_FORM_STEPS: { id: SupplyFormStep; label: string }[] = [
  { id: 'identity', label: '📦 Supply Identity' },
  { id: 'material', label: '🧱 Material & Physical Details' },
  { id: 'productionMethods', label: '🏭 Production' },
  { id: 'tracking', label: '📦 Purchasing' },
  { id: 'productCompatibility', label: 'Product Fit' },
  { id: 'photo', label: '🖼 Photos' },
];

export const SUPPLY_CLASSES: {
  value: SupplyClass;
  label: string;
  emoji: string;
  description: string;
}[] = [
  { value: 'CUSTOMIZABLE_BLANK', label: 'Blank', emoji: '🧱', description: 'Dog tags, acrylics, ornaments, tumblers — substrates decorated during production' },
  { value: 'CONSUMABLE', label: 'Consumable', emoji: '🧪', description: 'Filament, ink, vinyl, thread — used up over time' },
  { value: 'HARDWARE_COMPONENT', label: 'Hardware', emoji: '🔩', description: 'Key rings, clips, fasteners, magnets, chains' },
  { value: 'PACKAGING', label: 'Packaging', emoji: '📦', description: 'Bags, boxes, wrapping' },
  { value: 'BLANK_APPAREL', label: 'Blank Apparel', emoji: '👕', description: 'Unprinted shirts, hoodies, hats' },
  { value: 'ELECTRONIC', label: 'Electronic', emoji: '⚡', description: 'Batteries, LEDs, wiring' },
  { value: 'RAW_MATERIAL', label: 'Raw Material', emoji: '🧵', description: 'Sheets, lumber, stock material' },
  { value: 'MAINTENANCE_PART', label: 'Maintenance', emoji: '🛠️', description: 'Nozzles, build plates, upkeep' },
  { value: 'ACCESSORY', label: 'Accessory', emoji: '📎', description: 'Embellishments and add-ons' },
];

export const CRAFT_TYPES_BY_CLASS: Record<SupplyClass, { value: SupplyType; label: string }[]> = {
  CUSTOMIZABLE_BLANK: [
    { value: 'ACRYLIC_BLANK', label: 'Acrylic Blank' },
    { value: 'BUTTON_BLANK', label: 'Button Blank' },
    { value: 'COASTER_BLANK', label: 'Coaster Blank' },
    { value: 'DECAL_BLANK', label: 'Decal Blank' },
    { value: 'DOG_TAG_BLANK', label: 'Dog Tag Blank' },
    { value: 'KEYCHAIN_BLANK', label: 'Keychain Blank' },
    { value: 'MAGNET_BLANK', label: 'Magnet Blank' },
    { value: 'TUMBLER_BLANK', label: 'Mug / Tumbler / Travel / Stainless' },
    { value: 'ORNAMENT_BLANK', label: 'Ornament Blank' },
    { value: 'OTHER_BLANK', label: 'Other Blank' },
    { value: 'PATCH_BLANK', label: 'Patch Blank' },
    { value: 'SIGN_BLANK', label: 'Sign Blank' },
    { value: 'STICKER_BLANK', label: 'Sticker Blank' },
    { value: 'TOKEN_BLANK', label: 'Token Blank' },
    { value: 'TROPHY_BLANK', label: 'Trophy Blank' },
  ],
  BLANK_APPAREL: [
    { value: 'T_SHIRT', label: 'T-Shirt' },
    { value: 'TANK_TOP', label: 'Tank Top' },
    { value: 'HOODIE', label: 'Hoodie' },
    { value: 'SWEATSHIRT', label: 'Sweatshirt' },
    { value: 'POLO', label: 'Polo' },
    { value: 'HAT', label: 'Hat' },
    { value: 'BEANIE', label: 'Beanie' },
    { value: 'APRON', label: 'Apron' },
    { value: 'TOTE_BAG', label: 'Tote Bag' },
    { value: 'APPAREL_PATCH', label: 'Patch' },
    { value: 'OTHER_APPAREL', label: 'Other Apparel' },
  ],
  CONSUMABLE: [
    { value: 'FILAMENT', label: 'Filament' },
    { value: 'UV_INK', label: 'UV Ink' },
    { value: 'SUBLIMATION_INK', label: 'Sublimation Ink' },
    { value: 'THREAD', label: 'Embroidery Thread' },
    { value: 'VINYL', label: 'Vinyl' },
    { value: 'HEAT_TRANSFER', label: 'Heat Transfer Vinyl (HTV)' },
    { value: 'TRANSFER_TAPE', label: 'Transfer Tape' },
    { value: 'LASER_MATERIAL', label: 'Laser Material' },
    { value: 'SUBLIMATION_PAPER', label: 'Sublimation Paper' },
    { value: 'ADHESIVE', label: 'Adhesive' },
    { value: 'RESIN', label: 'Resin' },
    { value: 'CLEANING_FLUID', label: 'Cleaning Fluid' },
    { value: 'LUBRICANT', label: 'Lubricant' },
    { value: 'SOLVENT', label: 'Solvent' },
    { value: 'PRINTER_INK', label: 'Printer Ink' },
    { value: 'TONER', label: 'Toner' },
    { value: 'OTHER_CONSUMABLE', label: 'Other Consumable' },
  ],
  HARDWARE_COMPONENT: [
    { value: 'BOLT', label: 'Bolt' },
    { value: 'CHAIN', label: 'Chain' },
    { value: 'CLASP', label: 'Clasp' },
    { value: 'FASTENER', label: 'Fastener' },
    { value: 'HINGE', label: 'Hinge' },
    { value: 'HOOK', label: 'Hook' },
    { value: 'JUMP_RING', label: 'Jump Ring' },
    { value: 'KEY_RING', label: 'Key Ring' },
    { value: 'LED', label: 'LED' },
    { value: 'MAGNET', label: 'Magnet' },
    { value: 'NUT', label: 'Nut' },
    { value: 'OTHER_HARDWARE', label: 'Other Hardware' },
    { value: 'PIN_BACK', label: 'Pin Back' },
    { value: 'SCREW', label: 'Screw' },
    { value: 'WASHER', label: 'Washer' },
  ],
  PACKAGING: [
    { value: 'POLY_BAG', label: 'Poly Bag' },
    { value: 'ZIP_BAG', label: 'Zip Bag' },
    { value: 'BUBBLE_MAILER', label: 'Bubble Mailer' },
    { value: 'SHIPPING_BOX', label: 'Shipping Box' },
    { value: 'GIFT_BOX', label: 'Gift Box' },
    { value: 'PRODUCT_INSERT', label: 'Product Insert' },
    { value: 'THANK_YOU_CARD', label: 'Thank You Card' },
    { value: 'LABEL_SUPPLY', label: 'Label' },
    { value: 'PACKING_SLIP', label: 'Packing Slip' },
    { value: 'TISSUE_PAPER', label: 'Tissue Paper' },
    { value: 'SHRINK_WRAP', label: 'Shrink Wrap' },
    { value: 'OTHER_PACKAGING', label: 'Other Packaging' },
  ],
  ELECTRONIC: [
    { value: 'LED_MODULE', label: 'LED Module' },
    { value: 'LED_STRIP', label: 'LED Strip' },
    { value: 'BATTERY', label: 'Battery' },
    { value: 'POWER_SUPPLY', label: 'Power Supply' },
    { value: 'USB_CABLE', label: 'USB Cable' },
    { value: 'SWITCH', label: 'Switch' },
    { value: 'CONTROLLER', label: 'Controller' },
    { value: 'CHARGER', label: 'Charger' },
    { value: 'DISPLAY', label: 'Display' },
    { value: 'SENSOR', label: 'Sensor' },
    { value: 'OTHER_ELECTRONIC', label: 'Other Electronic' },
  ],
  RAW_MATERIAL: [
    { value: 'ACRYLIC_SHEET', label: 'Acrylic Sheet' },
    { value: 'MDF_SHEET', label: 'MDF Sheet' },
    { value: 'PLYWOOD_SHEET', label: 'Plywood Sheet' },
    { value: 'LEATHER_SHEET', label: 'Leather Sheet' },
    { value: 'STAINLESS_STEEL_SHEET', label: 'Stainless Steel Sheet' },
    { value: 'ALUMINUM_SHEET', label: 'Aluminum Sheet' },
    { value: 'FABRIC_MATERIAL', label: 'Fabric' },
    { value: 'CORK_MATERIAL', label: 'Cork' },
    { value: 'RUBBER_MATERIAL', label: 'Rubber' },
    { value: 'FOAM_MATERIAL', label: 'Foam' },
    { value: 'OTHER_RAW_MATERIAL', label: 'Other Material' },
  ],
  MAINTENANCE_PART: [
    { value: 'NOZZLE', label: 'Nozzle' },
    { value: 'BUILD_PLATE', label: 'Build Plate' },
    { value: 'PTFE_TUBE', label: 'PTFE Tube' },
    { value: 'EXTRUDER_GEAR', label: 'Extruder Gear' },
    { value: 'BELT', label: 'Belt' },
    { value: 'BEARING', label: 'Bearing' },
    { value: 'CLEANING_BRUSH', label: 'Cleaning Brush' },
    { value: 'MAINTENANCE_KIT', label: 'Maintenance Kit' },
    { value: 'REPLACEMENT_BLADE', label: 'Replacement Blade' },
    { value: 'FILTER', label: 'Filter' },
    { value: 'OTHER_MAINTENANCE', label: 'Other Maintenance' },
  ],
  ACCESSORY: [
    { value: 'CHARM', label: 'Charm' },
    { value: 'PENDANT', label: 'Pendant' },
    { value: 'ORNAMENT_ATTACHMENT', label: 'Ornament Attachment' },
    { value: 'DECORATIVE_BEAD', label: 'Decorative Bead' },
    { value: 'RIBBON', label: 'Ribbon' },
    { value: 'LANYARD', label: 'Lanyard' },
    { value: 'CLIP_ACCESSORY', label: 'Clip' },
    { value: 'STAND', label: 'Stand' },
    { value: 'EASEL', label: 'Easel' },
    { value: 'DISPLAY_HOLDER', label: 'Display Holder' },
    { value: 'OTHER_ACCESSORY', label: 'Other Accessory' },
  ],
  OTHER: [{ value: 'OTHER', label: 'Other' }],
};

export const MATERIAL_TYPES = [
  'Stainless Steel', 'Aluminum', 'Brass', 'Copper', 'Steel',
  'Acrylic', 'PLA', 'PLA+', 'PETG', 'TPU', 'ABS', 'ASA', 'Resin',
  'MDF', 'Plywood', 'Hardwood', 'Bamboo', 'Cork',
  'Cotton', 'Polyester', 'Cotton / Polyester', 'Cotton / Nylon', 'Canvas', 'Felt',
  'Leather', 'Glass', 'Ceramic', 'Silicone', 'Paper', 'Cardboard',
] as const;

export const FINISH_OPTIONS = [
  'Matte', 'Glossy', 'Satin', 'Silk', 'Shiny', 'Metallic', 'Glitter', 'Frosted', 'Transparent', 'Translucent', 'Rainbow', 'Raw',
] as const;

export const COLOR_OPTIONS = [
  'Red', 'White', 'Blue', 'Rainbow', 'Orange', 'Gray', 'Black', 'Silver', 'Gold', 'Green', 'Purple', 'Brown', 'Pink', 'Clear',
] as const;

export const FINISH_OPTIONS_BY_MATERIAL: Partial<Record<string, readonly string[]>> = {
  Aluminum: ['Raw', 'Brushed', 'Satin', 'Polished'],
  'Stainless Steel': ['Raw', 'Brushed', 'Satin', 'Polished'],
  Brass: ['Raw', 'Brushed', 'Polished'],
  Copper: ['Raw', 'Brushed', 'Polished'],
  Steel: ['Raw', 'Brushed', 'Satin', 'Polished'],
  PLA: ['Matte', 'Silk', 'Shiny', 'Metallic', 'Glitter', 'Rainbow'],
  'PLA+': ['Matte', 'Silk', 'Shiny', 'Metallic', 'Glitter', 'Rainbow'],
  PETG: ['Matte', 'Shiny', 'Transparent', 'Translucent'],
  TPU: ['Matte', 'Glossy'],
  ABS: ['Matte', 'Glossy'],
  ASA: ['Matte', 'Glossy'],
  Acrylic: ['Glossy', 'Frosted', 'Transparent'],
  Resin: ['Matte', 'Glossy', 'Transparent'],
  MDF: ['Raw', 'Sanded', 'Painted', 'Stained', 'Sealed'],
  Plywood: ['Raw', 'Sanded', 'Painted', 'Stained', 'Sealed'],
  Hardwood: ['Raw', 'Sanded', 'Painted', 'Stained', 'Sealed'],
  Bamboo: ['Raw', 'Sanded', 'Stained', 'Sealed'],
  Cork: ['Raw', 'Matte', 'Sealed'],
  Ceramic: ['Glossy', 'Satin', 'Raw'],
  Glass: ['Glossy', 'Frosted', 'Transparent'],
  Leather: ['Matte', 'Raw'],
  Cardboard: ['Matte', 'Raw'],
  Paper: ['Matte', 'Glossy', 'Satin'],
};

export const SURFACE_TREATMENT_OPTIONS = [
  'DTF',
  'UV',
  'Vinyl Print',
  'HTV Heat Transfer Vinyl',
  'Embroidery',
  'Fabric Painting',
  'Stencilling',
  'Tie-Dye',
  'Powder Coated',
] as const;

export const SHAPE_OPTIONS = [
  'Rectangle', 'Square', 'Circle', 'Oval', 'Bone', 'Paw', 'Heart', 'Shield', 'Custom',
] as const;

export const DIMENSION_UNITS = [
  { value: 'in', label: 'Inches' },
  { value: 'mm', label: 'Millimeters' },
  { value: 'cm', label: 'Centimeters' },
] as const;

export const WEIGHT_UNITS = [
  { value: 'g', label: 'Grams' },
  { value: 'oz', label: 'Ounces' },
  { value: 'lb', label: 'Pounds' },
  { value: 'kg', label: 'Kilograms' },
] as const;

export const APPAREL_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'] as const;

export const FILAMENT_MAKERS = [
  'Bambu Lab', 'Polymaker', 'Hatchbox', 'Overture', 'Sunlu', 'eSUN', 'Atomic', 'Proto-Pasta', 'Prusament', 'Other',
] as const;

export const FILAMENT_TYPES = ['ABS', 'ASA', 'Nylon', 'PC', 'PETG', 'PLA', 'PLA+', 'PVA', 'Support Material', 'TPU'] as const;

export const FILAMENT_DIAMETERS = ['1.75 mm', '2.85 mm'] as const;

export const FILAMENT_NET_WEIGHTS = ['250 g', '500 g', '750 g', '1 kg', '2 kg', '3 kg', '5 kg'] as const;

export const TUMBLER_BLANK_PROFILES = [
  {
    id: 'mug',
    label: 'Mug',
    materialType: 'Ceramic',
    finish: 'Glossy',
    sizes: [
      { label: '11 oz', height: '3.75', width: '3.25', depth: '3.25' },
      { label: '15 oz', height: '4.5', width: '3.4', depth: '3.4' },
    ],
  },
  {
    id: 'tumbler',
    label: 'Tumbler',
    materialType: 'Stainless Steel',
    finish: 'Raw',
    sizes: [
      { label: '12-16 oz', height: '6.5', width: '3', depth: '3' },
      { label: '24-30 oz', height: '8', width: '3.75', depth: '3.75' },
      { label: '40 oz & up', height: '10.5', width: '4', depth: '4' },
    ],
  },
  {
    id: 'travel',
    label: 'Travel',
    materialType: 'Stainless Steel',
    finish: 'Raw',
    sizes: [
      { label: '12-16 oz', height: '7', width: '3.25', depth: '3.25' },
      { label: '24-30 oz', height: '8.75', width: '3.75', depth: '3.75' },
      { label: '40 oz & up', height: '10.75', width: '4', depth: '4' },
    ],
  },
  {
    id: 'stainless',
    label: 'Stainless',
    materialType: 'Stainless Steel',
    finish: 'Raw',
    sizes: [
      { label: '2-14 oz', height: '4.5', width: '3', depth: '3' },
      { label: '16-20 oz', height: '7', width: '3.25', depth: '3.25' },
      { label: '30 oz', height: '8', width: '3.75', depth: '3.75' },
      { label: '40 oz', height: '10.5', width: '4', depth: '4' },
    ],
  },
] as const;

export const UNITS_OF_MEASURE = [
  { value: 'piece', label: 'Piece' },
  { value: 'gram', label: 'Gram (g)' },
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'ml', label: 'Milliliter (ml)' },
  { value: 'liter', label: 'Liter (L)' },
  { value: 'meter', label: 'Meter (m)' },
  { value: 'foot', label: 'Foot (ft)' },
  { value: 'inch', label: 'Inch (in)' },
  { value: 'sheet', label: 'Sheet' },
  { value: 'roll', label: 'Roll' },
  { value: 'box', label: 'Box' },
  { value: 'bag', label: 'Bag' },
  { value: 'set', label: 'Set' },
] as const;

export function isBlankSupplyClass(supplyClass: SupplyClass): boolean {
  return supplyClass === 'CUSTOMIZABLE_BLANK' || supplyClass === 'BLANK_APPAREL';
}

export function defaultUnitForClass(supplyClass: SupplyClass, supplyType: SupplyType): string {
  const allowed = allowedUnitsFor(supplyClass, supplyType);
  return allowed[0] ?? 'piece';
}

export function allowedUnitsFor(supplyClass: SupplyClass, supplyType: SupplyType): string[] {
  if (supplyType === 'FILAMENT') return ['gram', 'kg', 'roll'];
  if (['UV_INK', 'SUBLIMATION_INK'].includes(supplyType)) return ['ml', 'liter'];
  if (['VINYL', 'HEAT_TRANSFER', 'TRANSFER_TAPE', 'THREAD'].includes(supplyType)) return ['foot', 'inch', 'meter', 'roll'];
  if (['SUBLIMATION_PAPER', 'LASER_MATERIAL'].includes(supplyType)) return ['sheet', 'piece', 'roll'];
  if (isBlankSupplyClass(supplyClass)) return ['piece', 'box', 'bag', 'set'];
  if (supplyClass === 'HARDWARE_COMPONENT' || supplyClass === 'ACCESSORY') return ['piece', 'set', 'box', 'bag'];
  if (supplyClass === 'PACKAGING') return ['piece', 'box', 'bag', 'roll'];
  if (supplyClass === 'MAINTENANCE_PART') return ['piece', 'set'];
  if (supplyClass === 'RAW_MATERIAL') return ['piece', 'sheet', 'foot', 'kg', 'roll'];
  return ['piece', 'gram', 'kg', 'ml', 'meter', 'foot', 'inch', 'sheet', 'roll', 'box', 'bag', 'set'];
}

export function craftTypesForClass(supplyClass: SupplyClass): SupplyType[] {
  return CRAFT_TYPES_BY_CLASS[supplyClass].map((t) => t.value);
}

export function productionMethodMatchesSupplyClass(
  methodSupplyTypes: SupplyType[] | undefined,
  supplyClass: SupplyClass,
  supplyType: SupplyType
): boolean {
  if (!methodSupplyTypes?.length) return true;
  if (methodSupplyTypes.includes(supplyType)) return true;
  if (supplyClass === 'CUSTOMIZABLE_BLANK' && methodSupplyTypes.includes('BLANK_SUBSTRATE' as SupplyType)) return true;
  if (supplyClass === 'BLANK_APPAREL' && methodSupplyTypes.includes('BLANK_APPAREL' as SupplyType)) return true;
  return craftTypesForClass(supplyClass).some((t) => methodSupplyTypes.includes(t));
}

export function supplyClassFromType(type: SupplyType): SupplyClass {
  const map: Partial<Record<SupplyType, SupplyClass>> = {
    // Blanks
    BLANK_SUBSTRATE: 'CUSTOMIZABLE_BLANK',
    DOG_TAG_BLANK: 'CUSTOMIZABLE_BLANK',
    ACRYLIC_BLANK: 'CUSTOMIZABLE_BLANK',
    ORNAMENT_BLANK: 'CUSTOMIZABLE_BLANK',
    KEYCHAIN_BLANK: 'CUSTOMIZABLE_BLANK',
    TUMBLER_BLANK: 'CUSTOMIZABLE_BLANK',
    MAGNET_BLANK: 'CUSTOMIZABLE_BLANK',
    TROPHY_BLANK: 'CUSTOMIZABLE_BLANK',
    TOKEN_BLANK: 'CUSTOMIZABLE_BLANK',
    SIGN_BLANK: 'CUSTOMIZABLE_BLANK',
    BUTTON_BLANK: 'CUSTOMIZABLE_BLANK',
    PATCH_BLANK: 'CUSTOMIZABLE_BLANK',
    COASTER_BLANK: 'CUSTOMIZABLE_BLANK',
    STICKER_BLANK: 'CUSTOMIZABLE_BLANK',
    DECAL_BLANK: 'CUSTOMIZABLE_BLANK',
    OTHER_BLANK: 'CUSTOMIZABLE_BLANK',
    // Apparel
    BLANK_APPAREL: 'BLANK_APPAREL',
    SHIRT_BLANK: 'BLANK_APPAREL',
    HOODIE_BLANK: 'BLANK_APPAREL',
    HAT_BLANK: 'BLANK_APPAREL',
    T_SHIRT: 'BLANK_APPAREL',
    TANK_TOP: 'BLANK_APPAREL',
    HOODIE: 'BLANK_APPAREL',
    SWEATSHIRT: 'BLANK_APPAREL',
    POLO: 'BLANK_APPAREL',
    HAT: 'BLANK_APPAREL',
    BEANIE: 'BLANK_APPAREL',
    APRON: 'BLANK_APPAREL',
    TOTE_BAG: 'BLANK_APPAREL',
    APPAREL_PATCH: 'BLANK_APPAREL',
    OTHER_APPAREL: 'BLANK_APPAREL',
    // Consumables
    FILAMENT: 'CONSUMABLE',
    UV_INK: 'CONSUMABLE',
    SUBLIMATION_INK: 'CONSUMABLE',
    THREAD: 'CONSUMABLE',
    VINYL: 'CONSUMABLE',
    HEAT_TRANSFER: 'CONSUMABLE',
    TRANSFER_TAPE: 'CONSUMABLE',
    LASER_MATERIAL: 'CONSUMABLE',
    SUBLIMATION_PAPER: 'CONSUMABLE',
    ADHESIVE: 'CONSUMABLE',
    RESIN: 'CONSUMABLE',
    CLEANING_FLUID: 'CONSUMABLE',
    LUBRICANT: 'CONSUMABLE',
    SOLVENT: 'CONSUMABLE',
    PRINTER_INK: 'CONSUMABLE',
    TONER: 'CONSUMABLE',
    OTHER_CONSUMABLE: 'CONSUMABLE',
    // Hardware
    HARDWARE: 'HARDWARE_COMPONENT',
    KEY_RING: 'HARDWARE_COMPONENT',
    JUMP_RING: 'HARDWARE_COMPONENT',
    MAGNET: 'HARDWARE_COMPONENT',
    CHAIN: 'HARDWARE_COMPONENT',
    SCREW: 'HARDWARE_COMPONENT',
    BOLT: 'HARDWARE_COMPONENT',
    NUT: 'HARDWARE_COMPONENT',
    WASHER: 'HARDWARE_COMPONENT',
    LED: 'HARDWARE_COMPONENT',
    PIN_BACK: 'HARDWARE_COMPONENT',
    CLASP: 'HARDWARE_COMPONENT',
    HINGE: 'HARDWARE_COMPONENT',
    HOOK: 'HARDWARE_COMPONENT',
    FASTENER: 'HARDWARE_COMPONENT',
    OTHER_HARDWARE: 'HARDWARE_COMPONENT',
    // Packaging
    PACKAGING: 'PACKAGING',
    POLY_BAG: 'PACKAGING',
    ZIP_BAG: 'PACKAGING',
    BUBBLE_MAILER: 'PACKAGING',
    SHIPPING_BOX: 'PACKAGING',
    GIFT_BOX: 'PACKAGING',
    PRODUCT_INSERT: 'PACKAGING',
    THANK_YOU_CARD: 'PACKAGING',
    LABEL_SUPPLY: 'PACKAGING',
    PACKING_SLIP: 'PACKAGING',
    TISSUE_PAPER: 'PACKAGING',
    SHRINK_WRAP: 'PACKAGING',
    OTHER_PACKAGING: 'PACKAGING',
    // Electronic
    ELECTRONIC: 'ELECTRONIC',
    LED_MODULE: 'ELECTRONIC',
    LED_STRIP: 'ELECTRONIC',
    BATTERY: 'ELECTRONIC',
    POWER_SUPPLY: 'ELECTRONIC',
    USB_CABLE: 'ELECTRONIC',
    SWITCH: 'ELECTRONIC',
    CONTROLLER: 'ELECTRONIC',
    CHARGER: 'ELECTRONIC',
    DISPLAY: 'ELECTRONIC',
    SENSOR: 'ELECTRONIC',
    OTHER_ELECTRONIC: 'ELECTRONIC',
    // Raw Material
    RAW_MATERIAL: 'RAW_MATERIAL',
    ACRYLIC_SHEET: 'RAW_MATERIAL',
    MDF_SHEET: 'RAW_MATERIAL',
    PLYWOOD_SHEET: 'RAW_MATERIAL',
    LEATHER_SHEET: 'RAW_MATERIAL',
    STAINLESS_STEEL_SHEET: 'RAW_MATERIAL',
    ALUMINUM_SHEET: 'RAW_MATERIAL',
    FABRIC_MATERIAL: 'RAW_MATERIAL',
    CORK_MATERIAL: 'RAW_MATERIAL',
    RUBBER_MATERIAL: 'RAW_MATERIAL',
    FOAM_MATERIAL: 'RAW_MATERIAL',
    OTHER_RAW_MATERIAL: 'RAW_MATERIAL',
    // Maintenance
    MAINTENANCE_PART: 'MAINTENANCE_PART',
    NOZZLE: 'MAINTENANCE_PART',
    BUILD_PLATE: 'MAINTENANCE_PART',
    PTFE_TUBE: 'MAINTENANCE_PART',
    EXTRUDER_GEAR: 'MAINTENANCE_PART',
    BELT: 'MAINTENANCE_PART',
    BEARING: 'MAINTENANCE_PART',
    CLEANING_BRUSH: 'MAINTENANCE_PART',
    MAINTENANCE_KIT: 'MAINTENANCE_PART',
    REPLACEMENT_BLADE: 'MAINTENANCE_PART',
    FILTER: 'MAINTENANCE_PART',
    OTHER_MAINTENANCE: 'MAINTENANCE_PART',
    // Accessory
    ACCESSORY: 'ACCESSORY',
    CHARM: 'ACCESSORY',
    PENDANT: 'ACCESSORY',
    ORNAMENT_ATTACHMENT: 'ACCESSORY',
    DECORATIVE_BEAD: 'ACCESSORY',
    RIBBON: 'ACCESSORY',
    LANYARD: 'ACCESSORY',
    CLIP_ACCESSORY: 'ACCESSORY',
    STAND: 'ACCESSORY',
    EASEL: 'ACCESSORY',
    DISPLAY_HOLDER: 'ACCESSORY',
    OTHER_ACCESSORY: 'ACCESSORY',
    // Other
    OTHER: 'OTHER',
  };
  return map[type] ?? 'OTHER';
}

export function visibleSteps(_supplyClass: SupplyClass): SupplyFormStep[] {
  return [
    'identity',
    'material',
    'productionMethods',
    'tracking',
    'photo',
  ];
}
